import Big from 'big.js';
import type {BigSource, RoundingMode} from 'big.js';

export const numUtil = {
  floor(num, precision, scale = 0) {
    if (isNaN(num) || num === '' || num === Infinity) {
      return '';
    }
    const side = num < 0 ? -1 : 1;
    return Big(num).abs().times(Big(10).pow(-scale)).round(precision, 0).times(side).toFixed(precision);
  },
  round(num, precision, scale = 0) {
    if (isNaN(num) || num === '') {
      return '';
    }
    const side = num < 0 ? -1 : 1;
    return Big(num).abs().times(Big(10).pow(-scale)).round(precision).times(side).toFixed(precision);
  },
  ceil(num, precision, scale = 0) {
    if (isNaN(num) || num === '') {
      return '';
    }
    const side = num < 0 ? -1 : 1;
    return Big(num).abs().times(Big(10).pow(-scale)).round(precision, 3).times(side).toFixed(precision);
  },
  roundPriceEp(priceEp, tickSizeEp) {
    return Number(Big(priceEp).div(tickSizeEp).round().times(tickSizeEp));
  },
  delimit(value) {
    const amount = String(value);
    let regexp = '(\\d)(?=(\\d{3})+\\.)';
    if (amount.indexOf('.') < 0) {
      regexp = '(\\d)(?=(\\d{3})+$)';
    }
    return amount.replace(new RegExp(regexp, 'g'), '$1,');
  },
  pad(num) {
    return (num < 10 ? '0' : '') + String(num);
  },
  priceToEp(price, scale = 4) {
    if (isNaN(price) || price === '' || price === 0) {
      return 0;
    }
    const factor = Big(10).pow(scale);
    return Number(Big(String(price).trim()).times(factor));
  },
  abbreviateNumber(num: number, precision = 2): string {
    const str = num.toString();
    if (num < 1e3) {
      return this.floor(str, precision);
    }

    const items = [
      {number: 1e3, unit: 'K'},
      {number: 1e6, unit: 'M'},
      {number: 1e9, unit: 'B'},
      {number: 1e12, unit: 'T'},
      {number: 1e15, unit: 'P'},
      {number: 1e18, unit: 'E'},
    ];

    let index;
    for (index = items.length - 1; index > 0; index--) {
      if (num >= items[index].number) {
        break;
      }
    }
    const item = items[index];
    if (!item) {
      return str;
    }
    const {number, unit} = item;
    return this.floor((num / number).toString().replace(/\.0+$|(\.[0-9]*[1-9])0+$/, '$1'), precision) + unit;
  },
  unAbbreviateNumber(num) {
    const map = {K: 1e3, M: 1e6, B: 1e9, T: 1e12, P: 1e15, E: 1e18};
    const realNum = ('' + num).replace(/,/g, '');
    const key = Object.keys(map).find((o) => realNum.endsWith(o));
    if (key) {
      return parseFloat(realNum.slice(0, -1)) * map[key];
    }
    return parseFloat(realNum);
  },
  startWithNumber(str) {
    return /^\d/.test(str);
  },
  parseInt(str: string) {
    const map = {K: 1e3, M: 1e6, B: 1e9, T: 1e12, P: 1e15, E: 1e18};
    const key = Object.keys(map).find((o) => str.endsWith(o));
    if (key) {
      return parseInt(str.slice(0, -1)) * map[key];
    }
    return parseInt(str);
  },
  trimEnd0(str: string) {
    return str.replace(/(\.\d*?)0+$/g, '$1').replace(/\.$/, '');
  },
};

export function floor(val, precision = 0): number {
  return Number(Big(val).round(precision, Big.roundDown));
}

/**
 * value1 - value2
 * @param {BigSource} value1
 * @param {BigSource} value2
 * @returns
 */
export function minus(value1: BigSource, value2: BigSource): string {
  return toString(Big(value1).minus(value2));
}

/**
 * value1 + value2
 * @param {BigSource} value1
 * @param {BigSource} value2
 * @returns
 */
export function plus(value1: BigSource, value2: BigSource): string {
  return toString(Big(value1).plus(value2));
}

/**
 * value1 + value2 + ...
 * @param {BigSource[]} values
 * @returns
 */
export function sum(...values: BigSource[]): string {
  if (values.length < 1) {
    return '0';
  }
  return toString(values.reduce((p, c) => Big(p).plus(c), Big(0)));
}

/**
 * value1 * value2
 * @param {BigSource} value1
 * @param {BigSource} value2
 * @returns
 */
export function times(value1: BigSource, value2: BigSource): string {
  return toString(Big(value1).times(value2));
}

/**
 * value1 / value2
 * @param {BigSource} value1
 * @param {BigSource} value2
 * @param {number} precision -1 不截取、0 到 1e+6（含） 保留小数位数。Default value: -1
 * @param {RoundingMode} rm 舍入模式：0（向下）、1（向最近的邻居四舍五入。如果等距，则从零舍入。）、2（向最近的邻居四舍五入。如果等距，则向偶数邻居舍入。）、 3（向上）。Default value: 1
 * @returns
 */
export function div(value1: BigSource, value2: BigSource, precision: number = -1, rm: RoundingMode = 1): string {
  if (precision === -1) {
    return toString(Big(value1).div(value2));
  }
  return toFixed(Big(value1).div(value2), precision, rm);
}

export function min(value1: BigSource, ...values: BigSource[]): string {
  if (values.length === 0) {
    return toString(value1);
  }
  return toString(values.reduce((p: Big, c) => (p.gt(c) ? Big(c) : p), Big(value1)));
}

export function max(value1: BigSource, ...values: BigSource[]): string {
  if (values.length === 0) {
    return toString(value1);
  }
  return toString(values.reduce((p: Big, c: BigSource) => (p.gt(c) ? p : Big(c)), Big(value1)));
}

/**
 * value1 > value2 => true
 * @param {BigSource} value1
 * @param {BigSource} value2
 * @returns
 */
export function gt(value1: BigSource, value2: BigSource): boolean {
  return Big(value1).gt(value2);
}

/**
 * value1 >= value2 => true
 * @param {BigSource} value1
 * @param {BigSource} value2
 * @returns
 */
export function gte(value1: BigSource, value2: BigSource): boolean {
  return Big(value1).gte(value2);
}

/**
 * value1 < value2 => true
 * @param {BigSource} value1
 * @param {BigSource} value2
 * @returns
 */
export function lt(value1: BigSource, value2: BigSource): boolean {
  return Big(value1).lt(value2);
}

/**
 * value1 <= value2 => true
 * @param {BigSource} value1
 * @param {BigSource} value2
 * @returns
 */
export function lte(value1: BigSource, value2: BigSource): boolean {
  return Big(value1).lte(value2);
}

/**
 * Big(value1) === Big(value2) => true
 * @param {BigSource} value1
 * @param {BigSource} value2
 * @returns
 */
export function eq(value1: BigSource, value2: BigSource): boolean {
  return Big(value1).eq(value2);
}

/**
 * Math.pow(10).(exp)
 * @param {BigSource} exp
 * @param {boolean} isToString
 * @returns
 */
export function pow10(exp: BigSource, isToString = false): Big | string {
  const res = Big(10).pow(Big(exp).toNumber());
  if (isToString) {
    return toString(res);
  }
  return res;
}

/**
 * @param {BigSource} value
 * @param {number} precision 小数位数，0 到 1e+6（含）
 * @param {RoundingMode} rm 舍入模式：0（向下）、1（向最近的邻居四舍五入。如果等距，则从零舍入。）、2（向最近的邻居四舍五入。如果等距，则向偶数邻居舍入。）、 3（向上）。Default value: 1
 * @returns
 */
export function toFixed(value: BigSource, precision = 8, rm: RoundingMode = 1): string {
  return Big(value).round(precision, rm).toFixed(precision);
}

/**
 * Math.abs(number)
 * @param {BigSource} value
 */
export function abs(value: BigSource): string {
  return toString(Big(value).abs());
}

/**
 * 将 BigSource 类型数据转为字符串。（Big.js 的 toString 会产生科学计数法数字字符串）
 * @param {BigSource} value
 * @returns
 */
export function toString(value: BigSource): string {
  const str = Big(value).abs().toString();
  // 单位（正/负）
  const side = Big(value).gte(0) ? '' : '-';
  if (!/e/.test(str)) {
    return side + str;
  }

  // 指数
  const index = Number(str.match(/\d+$/)[0]);
  // 基数
  const basis = str.match(/^[\d\.]+/)[0].replace(/\./, '');

  if (/e-/.test(str)) {
    return side + basis.padStart(index + basis.length, '0').replace(/^0/, '0.');
  }

  if (basis.length < index) {
    return side + basis.padEnd(index + 1, '0');
  }

  const integerPart = basis.substring(0, index + 1);
  const decimalPart = basis.substring(index + 1);

  if (decimalPart) {
    return side + integerPart + '.' + decimalPart;
  }

  return side + integerPart;
}

export {div as divided};
